import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  KeyboardTimePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/styles';
import availableTimeZones from './availableTimeZones';
import Skeleton from 'react-loading-skeleton';
import get from 'lodash.get';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Divider,
  Typography,
  TextField
} from '@material-ui/core';
import {
  convertToTimeOnlyString,
  convertToMomentTime
} from 'helpers/timeUtils';

const useStyles = makeStyles(theme => ({
  root: {},
  item: {
    display: 'flex',
    flexDirection: 'column'
  },
  timeInputContainer: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  }
}));

const DateTimeSettings = props => {
  const { className, useMetaDataSettings, dateTimeSettingsContent, ...rest } = props;

  const {
    DEFAULT_TIME_ZONE_PATH,
    CHECK_IN_TIME,
    CHECK_OUT_TIME,
    SMS_COUNTRY_CODE,
    handleChange,
    settingsMetaData,
    isSettingsMetaDataUpdating,
    isLoading,
    onBlur,
    onFocus,
    shouldShowError,
    getErrorMessage
  } = useMetaDataSettings;

  const settingsDefaultTimeZone = get(settingsMetaData, DEFAULT_TIME_ZONE_PATH, null);

  const settingsCheckInTime = get(settingsMetaData, CHECK_IN_TIME, null);

  const settingsCheckOutTime = get(settingsMetaData, CHECK_OUT_TIME, null);

  const settingsSmsCountryCode = get(settingsMetaData, SMS_COUNTRY_CODE, null);

  const getOptionDefaultTimeZoneLabel = option => {
    if (option === settingsDefaultTimeZone) {
      return `${option} - Default`;
    }
    return option;
  };

  const classes = useStyles();

  const SkeletonCardContent = () => {
    return (
      <CardContent>
        <Grid item md={6} xs={12}>
          <Typography display="block" style={{ fontSize: '2.2rem' }}>
            <Skeleton />
          </Typography>
        </Grid>
      </CardContent>
    );
  };

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <form>
        <CardHeader
          subheader={dateTimeSettingsContent?.description || `Manage default settings`}
          title={dateTimeSettingsContent?.title || `Default Settings`}
        />
        <Divider />
        {isLoading || isSettingsMetaDataUpdating ? (
          <SkeletonCardContent />
        ) : (
          <CardContent>
            <Autocomplete
              id="default-timezone"
              name={DEFAULT_TIME_ZONE_PATH}
              options={availableTimeZones}
              getOptionLabel={getOptionDefaultTimeZoneLabel}
              style={{ width: 300 }}
              onBlur={() => onBlur(DEFAULT_TIME_ZONE_PATH)}
              onFocus={() => onFocus(DEFAULT_TIME_ZONE_PATH)}
              onChange={(e, option) =>
                handleChange({
                  target: {
                    name: DEFAULT_TIME_ZONE_PATH,
                    value: option
                  }
                })
              }
              value={settingsDefaultTimeZone}
              renderInput={params => (
                <TextField
                  {...params}
                  error={shouldShowError(DEFAULT_TIME_ZONE_PATH)}
                  helperText={getErrorMessage(DEFAULT_TIME_ZONE_PATH)}
                  label={dateTimeSettingsContent?.timeZoneDropdownLabel || `Time Zone`}
                  variant="outlined"
                />
              )}
            />
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <Grid item>
                <KeyboardTimePicker
                  className={classes.timeInputContainer}
                  style={{ width: 145 }}
                  margin="dense"
                  id="time-in-picker"
                  name="checkIn"
                  label={dateTimeSettingsContent?.checkInLabel || `Check In`}
                  value={convertToMomentTime(settingsCheckInTime)}
                  onChange={time =>
                    handleChange({
                      target: {
                        name: CHECK_IN_TIME,
                        value: convertToTimeOnlyString(time)
                      }
                    })
                  }
                  onBlur={() => onBlur(CHECK_IN_TIME)}
                  onFocus={() => onFocus(CHECK_IN_TIME)}
                  KeyboardButtonProps={{
                    'aria-label': 'change time'
                  }}
                  helperText={getErrorMessage(CHECK_IN_TIME)}
                />
                <KeyboardTimePicker
                  className={classes.timeInputContainer}
                  style={{ width: 145 }}
                  margin="dense"
                  id="time-out-picker"
                  name="checkOut"
                  label={dateTimeSettingsContent?.checkOutLabel || `Check Out`}
                  value={convertToMomentTime(settingsCheckOutTime)}
                  onChange={time =>
                    handleChange({
                      target: {
                        name: CHECK_OUT_TIME,
                        value: convertToTimeOnlyString(time)
                      }
                    })
                  }
                  onBlur={() => onBlur(CHECK_OUT_TIME)}
                  onFocus={() => onFocus(CHECK_OUT_TIME)}
                  KeyboardButtonProps={{
                    'aria-label': 'change time'
                  }}
                  helperText={getErrorMessage(CHECK_OUT_TIME)}
                />
              </Grid>
            </MuiPickersUtilsProvider>
            <Grid item style={{ marginTop: '1rem', display: 'flex', alignItems: 'center' }}>
                <TextField
                  name={SMS_COUNTRY_CODE}
                  label={dateTimeSettingsContent?.smsCountryCodeLabel || "SMS Country Code"}
                  value={settingsSmsCountryCode || ''}
                  onChange={e => {
                    const value = e.target.value;
                    if (value && !/^\d*$/.test(value)) {
                        return;
                    }
                    handleChange({
                      target: {
                        name: SMS_COUNTRY_CODE,
                        value: value
                      }
                    })
                  }}
                  inputProps={{ maxLength: 4 }}
                  variant="outlined"
                  margin="dense"
                />
            </Grid>
          </CardContent>
        )}
      </form>
    </Card>
  );
};

DateTimeSettings.propTypes = {
  className: PropTypes.string,
  useDeviceContextSettings: PropTypes.object
};

export default DateTimeSettings;
